import {
  Box,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { useController } from "react-hook-form";
import {
  getCountryCallingCode,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { countryOptions } from "../../utility/constants";

// Derived from countryOptions so the picker can't drift from the countries we
// actually support. Sorted by label to match mobile's country picker order.
const COUNTRIES = countryOptions
  .map(({ value, label }) => ({
    code: value,
    label,
    calling: getCountryCallingCode(value),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const FlagImg = ({ code }) => (
  <Box
    component="img"
    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`}
    alt={code}
    sx={{ width: 22, height: 16, flexShrink: 0 }}
  />
);

// `validate` rule for the phone field: the number must be valid for the form's
// selected country (a typed "+44..." is validated as international).
export const validatePhoneForCountry = (value, formValues) =>
  isValidPhoneNumber(value || "", formValues.country) ||
  "Please enter a valid phone number";

// Country picker + national-number field in one bordered box, bound to the
// form's `country` and `phoneNumber` fields. Validation of the phone field is
// up to the caller via `rules`, since it differs (required at login, optional
// on the mailing list). `onChangeAfter` lets the form re-run validation of related
// fields when either control changes.
const CountryPhoneInput = ({
  control,
  rules,
  onChangeAfter,
  size = "medium",
  bgcolor = "white",
  focusColor = "#046287",
}) => {
  const { field: country } = useController({ name: "country", control });
  const {
    field: phone,
    fieldState: { error },
  } = useController({ name: "phoneNumber", control, rules });

  const selected =
    COUNTRIES.find((c) => c.code === country.value) || COUNTRIES[0];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          border: "1px solid",
          borderColor: error ? "error.main" : "#c4c4c4",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor,
          "&:focus-within": {
            borderColor: error ? "error.main" : focusColor,
            borderWidth: 2,
          },
        }}
      >
        <Select
          value={selected.code}
          onChange={(e) => {
            country.onChange(e.target.value);
            onChangeAfter?.();
          }}
          variant="standard"
          disableUnderline
          inputProps={{ "aria-label": "Phone number country" }}
          renderValue={() => (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1 }}
            >
              <FlagImg code={selected.code} />
              <Typography fontSize={13} color="text.secondary">
                +{selected.calling}
              </Typography>
            </Box>
          )}
          sx={{
            flexShrink: 0,
            borderRight: "1px solid #e5e7eb",
            bgcolor: "#fafafa",
            minWidth: 90,
            "& .MuiSelect-select": {
              py: size === "small" ? "10px" : "14px",
              pr: "28px !important",
              pl: 0,
            },
          }}
        >
          {COUNTRIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FlagImg code={c.code} />
                <Typography fontSize={14}>{c.label}</Typography>
                <Typography fontSize={13} color="text.secondary">
                  +{c.calling}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>

        <OutlinedInput
          {...phone}
          inputRef={phone.ref}
          size={size}
          placeholder="Phone number"
          type="tel"
          notched={false}
          label=""
          inputProps={{ "aria-label": "Phone number" }}
          onChange={(e) => {
            phone.onChange(e);
            onChangeAfter?.();
          }}
          sx={{
            flex: 1,
            "& fieldset": { border: "none" },
            fontSize: 15,
          }}
        />
      </Box>
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </Box>
  );
};

export default CountryPhoneInput;
